import { Routes } from '@angular/router';
import { provideAgrDialog } from '@next/core-lib/dialog';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';

import { BBACancellationEffects } from './bba-cancellation/store/bba-cancellation.effects';
import { bbaCancellationFeature } from './bba-cancellation/store/bba-cancellation.feature';
import { SalesOrderPeriodsService } from './sales-order-periods/services/sales-order-periods.service';
import { SalesOrderPeriodsStore } from './sales-order-periods/store/sales-order-periods.store';
import {
  BALANS_BEMESTING_CROP_FIELDS_ADMIN_APPLICATION_KEY,
  CROP_AID_ADMIN_APPLICATION_KEY,
  BLIGHT_INFECTION_APPLICATION_KEY,
  CROP_AID_SYNERGIES_APPLICATION_KEY,
  CROP_AID_ADMISSION_APPLICATION_KEY,
  FIELD_REPORT_APPLICATION_KEY,
  SPRAY_COCKTAILS_APPLICATION_KEY,
  TGA_VARIETIES_APPLICATION_KEY,
  USER_NOTIFICATIONS_APPLICATION_KEY,
  WATER_AUTHORITIES_APPLICATION_KEY,
  ARABLE_PERIOD_APPLICATION_KEY,
  PLANET_PROOF_APPLICATION_KEY,
  FIELD_ADVICE_ADMIN_APPLICATION_KEY,
} from './shared/constants';
import { CropAidAdmissionService } from './crop-aid-admission/services/crop-aid-admission.service';
import { CropAidAdmissionStore } from './crop-aid-admission/store/crop-aid-admission.store';
import { globalAdminForAppKeyGuard } from './shared/guards/global-admin-for-application-key.guard';
import { TGAVarietiesService } from './tga-varieties/services/tga-varieties.service';
import { TGAVarietiesEffects } from './tga-varieties/store/tga-varieties.effects';
import { tgaVarietiesFeature } from './tga-varieties/store/tga-varieties.feature';
import { NotificationService } from './user-notifications/services/notification.service';
import { NotificationEffects } from './user-notifications/store/notification.effects';
import { notificationFeature } from './user-notifications/store/notification.feature';
import { WaterAuthoritiesService } from './water-authorities/services/water-authorities.service';
import { WaterAuthoritiesEffects } from './water-authorities/store/water-authorities.effects';
import { waterAuthoritiesFeature } from './water-authorities/store/water-authorities.feature';

export const planetProofGlobalAdminGuard = globalAdminForAppKeyGuard(PLANET_PROOF_APPLICATION_KEY);

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./home/components/home/home.component').then((m) => m.HomeComponent),
    pathMatch: 'full',
  },
  {
    path: 'bba-cancellation',
    canActivate: [globalAdminForAppKeyGuard(BALANS_BEMESTING_CROP_FIELDS_ADMIN_APPLICATION_KEY)],
    loadComponent: () => import('./bba-cancellation/components/bba-cancellation/bba-cancellation.component').then((m) => m.BbaCancellationComponent),
    providers: [provideState(bbaCancellationFeature), provideEffects(BBACancellationEffects)],
  },
  {
    path: 'user-notifications',
    canActivate: [globalAdminForAppKeyGuard(USER_NOTIFICATIONS_APPLICATION_KEY)],
    providers: [NotificationService, provideState(notificationFeature), provideEffects(NotificationEffects)],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./user-notifications/components/user-notifications/user-notifications.component').then((m) => m.UserNotificationsComponent),
      },
      {
        path: 'new',
        loadComponent: () => import('./user-notifications/components/notification-form/notification-form.component').then((m) => m.NotificationFormComponent),
      },
      {
        path: ':id',
        loadComponent: () => import('./user-notifications/components/notification-form/notification-form.component').then((m) => m.NotificationFormComponent),
      },
    ],
  },
  {
    path: 'tga-varieties',
    canActivate: [globalAdminForAppKeyGuard(TGA_VARIETIES_APPLICATION_KEY)],
    loadComponent: () => import('./tga-varieties/components/tga-varieties/tga-varieties.component').then((m) => m.TgaVarietiesComponent),
    providers: [TGAVarietiesService, provideState(tgaVarietiesFeature), provideEffects(TGAVarietiesEffects)],
  },
  {
    path: 'water-authorities',
    canActivate: [globalAdminForAppKeyGuard(WATER_AUTHORITIES_APPLICATION_KEY)],
    loadComponent: () => import('./water-authorities/components/water-authorities/water-authorities.component').then((m) => m.WaterAuthoritiesComponent),
    providers: [WaterAuthoritiesService, provideState(waterAuthoritiesFeature), provideEffects(WaterAuthoritiesEffects)],
  },
  {
    path: 'crop-management',
    loadChildren: () => import('./crop-admin/crop-admin.routes').then((m) => m.CROP_ADMIN_ROUTES),
  },
  {
    path: 'crop-aid',
    canActivate: [globalAdminForAppKeyGuard(CROP_AID_ADMIN_APPLICATION_KEY)],
    children: [
      {
        path: '',
        loadComponent: () => import('./crop-aid/components/overview/overview.component').then((m) => m.CropAidOverviewComponent),
      },
      {
        path: 'new',
        loadComponent: () => import('./crop-aid/components/detail/detail.component').then((m) => m.CropAidDetailComponent),
      },
      {
        path: ':id',
        loadComponent: () => import('./crop-aid/components/detail/detail.component').then((m) => m.CropAidDetailComponent),
      },
    ],
  },
  {
    path: 'admission',
    canActivate: [globalAdminForAppKeyGuard(CROP_AID_ADMISSION_APPLICATION_KEY)],
    loadComponent: () => import('./crop-aid-admission/components/admission-list/admission-list.component').then((m) => m.AdmissionListComponent),
    providers: [provideAgrDialog(), CropAidAdmissionService, CropAidAdmissionStore],
  },
  {
    path: 'ingredient',
    canActivate: [globalAdminForAppKeyGuard(CROP_AID_ADMISSION_APPLICATION_KEY)],
    loadComponent: () => import('./crop-aid-admission/components/ingredient-list/ingredient-list.component').then((m) => m.IngredientListComponent),
    providers: [provideAgrDialog(), CropAidAdmissionService, CropAidAdmissionStore],
  },
  {
    path: 'cropaid-synergies',
    canActivate: [globalAdminForAppKeyGuard(CROP_AID_SYNERGIES_APPLICATION_KEY)],
    loadComponent: () => import('./cropaid-synergies/components/cropaid-synergies/cropaid-synergies.component').then((m) => m.CropAidSynergiesComponent),
  },
  {
    path: 'spray-cocktails',
    canActivate: [globalAdminForAppKeyGuard(SPRAY_COCKTAILS_APPLICATION_KEY)],
    children: [
      {
        path: '',
        loadComponent: () => import('./spray-cocktails/components/overview/overview.component').then((m) => m.SprayCocktailsOverviewComponent),
      },
      {
        path: 'new',
        loadComponent: () => import('./spray-cocktails/components/detail/detail.component').then((m) => m.SprayCocktailDetailComponent),
      },
      {
        path: ':id',
        loadComponent: () => import('./spray-cocktails/components/detail/detail.component').then((m) => m.SprayCocktailDetailComponent),
      },
    ],
  },
  {
    path: 'blight-infection',
    canActivate: [globalAdminForAppKeyGuard(BLIGHT_INFECTION_APPLICATION_KEY)],
    loadComponent: () => import('./blight-infection/components/overview/overview.component').then((m) => m.BlightInfectionOverviewComponent),
  },
  {
    path: 'field-report',
    canActivate: [globalAdminForAppKeyGuard(FIELD_REPORT_APPLICATION_KEY)],
    loadChildren: () => import('./field-report/field-report.module').then((m) => m.FieldReportModule),
  },
  {
    path: 'planet-proof',
    canActivate: [planetProofGlobalAdminGuard],
    loadChildren: () => import('./planet-proof/planet-proof.module').then((m) => m.PlanetProofModule),
  },
  {
    path: 'field-advice',
    canActivate: [globalAdminForAppKeyGuard(FIELD_ADVICE_ADMIN_APPLICATION_KEY)],
    loadChildren: () => import('./field-advice/field-advice.routes').then((m) => m.FIELD_ADVICE_ROUTES),
  },
  {
    path: 'sales-order-schema',
    canActivate: [globalAdminForAppKeyGuard(ARABLE_PERIOD_APPLICATION_KEY)],
    loadComponent: () =>
      import('./sales-order-periods/components/overview/sales-order-periods-overview.component').then((m) => m.SalesOrderPeriodsOverviewComponent),
    providers: [provideAgrDialog(), SalesOrderPeriodsService, SalesOrderPeriodsStore],
  },
];
